import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useLazyQuery, gql } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import { event } from '../analytics';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  LinearProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Visibility,
  Download,
  Filter,
  Refresh,
  Error as ErrorIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  PlayArrow,
  CheckCircle,
  Cancel,
  Settings,
  Delete,
  PictureAsPdf,
  TableChart,
} from '@mui/icons-material';
import { SEQUENCE_EXECUTIONS_QUERY, SequenceExecutionResult, EXPORT_EXECUTION_CSV_QUERY, EXPORT_SEQUENCE_CSV_QUERY } from '../graphql/results';

// Mutation for soft-deleting executions
const DELETE_EXECUTION_MUTATION = gql`
  mutation DeleteExecution($executionId: ID!) {
    deleteExecution(executionId: $executionId)
  }
`;

// Helper function to format milliseconds into minutes and seconds
const formatDuration = (ms: number): string => {
  if (!ms || ms === 0) return '0s';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

// RAG Explanation Tooltip Component with toggle between fix modes
interface RagExplanation {
  fix_response: string;
  fix_query_context: string;
}

interface RagExplanationTooltipProps {
  label: string;
  hasIssue: boolean;
  noIssue: boolean;
  isNull: boolean;
  explanation: RagExplanation | null;
}

  const RagExplanationTooltip: React.FC<RagExplanationTooltipProps> = ({ 
  label, 
  hasIssue, 
  noIssue, 
  isNull,
  explanation 
}) => {
  const [showInverted, setShowInverted] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  
  const content = (
    <Typography 
      variant="caption" 
      sx={{ 
        display: 'block', 
        color: hasIssue ? 'error.main' : noIssue ? 'success.main' : 'text.secondary',
        cursor: hasIssue && explanation ? 'help' : 'default',
      }}
    >
      {hasIssue ? '❌' : noIssue ? '✅' : '⏺️'} {label}
      {isNull && ' (not checked)'}
    </Typography>
  );
  
  // If no issue or no explanation, just return the content
  if (!hasIssue || !explanation) {
    return content;
  }
  
  const currentExplanation = showInverted 
    ? explanation.fix_query_context 
    : explanation.fix_response;
  
  return (
    <Tooltip 
      title={
        <Box sx={{ p: 1.5, maxWidth: 450 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#22d3ee' }}>
              {showInverted ? '🔄 Fix Query/Context' : '🤖 Fix AI Response'}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                setShowInverted(!showInverted);
              }}
              sx={{ 
                fontSize: '0.65rem', 
                py: 0.25, 
                px: 1,
                color: '#5eead4',
                borderColor: '#5eead4',
                '&:hover': {
                  borderColor: '#fff',
                  color: '#fff',
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
                textTransform: 'none',
              }}
            >
              {showInverted ? '🤖 Fix Response' : '🔄 Invert Logic'}
            </Button>
          </Box>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {currentExplanation || 'No explanation available.'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#94a3b8', fontStyle: 'italic' }}>
            {showInverted 
              ? 'Showing how to fix by changing the User Query and/or Retrieved Context'
              : 'Showing how to fix by adjusting the AI Response'
            }
          </Typography>
        </Box>
      }
      arrow
      placement="left"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: '#0f172a',
            border: '1px solid #334155',
            '& .MuiTooltip-arrow': {
              color: '#0f172a',
            },
            maxWidth: 500,
          },
        },
      }}
    >
      {content}
    </Tooltip>
  );
};

const BiasResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const executionIdFromUrl = searchParams.get('executionId');
  
  // Initialize time range from sessionStorage or default to '30_days'
  const [timeRange, setTimeRange] = useState<string>(() => {
    const savedTimeRange = sessionStorage.getItem('biasResultsTimeRange');
    return savedTimeRange || '30_days';
  });
  
  // Per-sequence infinite scroll state - tracks how many items to show per sequence
  const INITIAL_ITEMS = 20;
  const LOAD_MORE_COUNT = 20;
  const [visibleCounts, setVisibleCounts] = useState<Record<string, number>>({});
  
  // Save time range to sessionStorage whenever it changes
  React.useEffect(() => {
    sessionStorage.setItem('biasResultsTimeRange', timeRange);
  }, [timeRange]);
  
  // Reset visible counts when time range changes
  React.useEffect(() => {
    setVisibleCounts({});
  }, [timeRange]);
  
  const { data: executionsData, loading: executionsLoading, error: executionsError, refetch } = useQuery(SEQUENCE_EXECUTIONS_QUERY, {
    variables: { 
      limit: executionIdFromUrl ? 1 : 10000, // Fetch all executions, infinite scroll in UI per sequence
      offset: 0,
      executionId: executionIdFromUrl || undefined,
      timeRange: timeRange
    },
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000, // Refresh every 60 seconds
    notifyOnNetworkStatusChange: false // Don't reset UI state on refresh
  });
  
  // Get total count from the query
  const totalExecutions = executionsData?.totalSequenceExecutions ?? 0;
  
  // Helper to get visible count for a sequence
  const getVisibleCount = (sequenceName: string) => visibleCounts[sequenceName] || INITIAL_ITEMS;
  const loadMore = useCallback((sequenceName: string) => {
    setVisibleCounts(prev => ({
      ...prev,
      [sequenceName]: (prev[sequenceName] || INITIAL_ITEMS) + LOAD_MORE_COUNT
    }));
  }, []);
  
  // Delete execution functionality
  const [deleteExecution] = useMutation(DELETE_EXECUTION_MUTATION);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [executionToDelete, setExecutionToDelete] = useState<{ id: string; name: string; timestamp: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  const handleDeleteClick = (e: React.MouseEvent, executionId: string, sequenceName: string, timestamp: string) => {
    e.stopPropagation(); // Prevent row expansion
    setExecutionToDelete({ id: executionId, name: sequenceName, timestamp });
    setDeleteConfirmOpen(true);
  };
  
  const confirmDelete = async () => {
    if (!executionToDelete) return;
    
    setDeleting(true);
    try {
      await deleteExecution({
        variables: { executionId: executionToDelete.id }
      });
      event('results_delete_execution', { event_category: 'results', event_action: 'delete' });
      setDeleteConfirmOpen(false);
      setExecutionToDelete(null);
      refetch(); // Refresh the list
    } catch (error) {
      console.error('Failed to delete execution:', error);
    } finally {
      setDeleting(false);
    }
  };

  // Export functionality
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [exportingExecution, setExportingExecution] = useState<{ id: string; sequenceId: string; name: string } | null>(null);
  const [exporting, setExporting] = useState(false);
  const executionExportRef = useRef<HTMLDivElement>(null);

  const [fetchExecutionCsv] = useLazyQuery(EXPORT_EXECUTION_CSV_QUERY, {
    fetchPolicy: 'network-only',
  });

  const [fetchSequenceCsv] = useLazyQuery(EXPORT_SEQUENCE_CSV_QUERY, {
    fetchPolicy: 'network-only',
  });

  const handleExportClick = (e: React.MouseEvent<HTMLElement>, executionId: string, sequenceId: string, sequenceName: string) => {
    e.stopPropagation();
    setExportingExecution({ id: executionId, sequenceId, name: sequenceName });
    setExportMenuAnchor(e.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportCsv = async () => {
    if (!exportingExecution) return;
    
    setExporting(true);
    handleExportMenuClose();
    
    try {
      const { data } = await fetchExecutionCsv({
        variables: { executionId: exportingExecution.id }
      });
      
      if (data?.exportExecutionCsv) {
        event('results_export_csv', { event_category: 'results', event_action: 'export_csv' });
        // Create and download CSV file
        const blob = new Blob([data.exportExecutionCsv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `risk-analysis-${exportingExecution.name}-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Failed to export CSV:', error);
    } finally {
      setExporting(false);
      setExportingExecution(null);
    }
  };

  const handleExportPdf = async () => {
    if (!exportingExecution) return;
    
    setExporting(true);
    handleExportMenuClose();
    
    try {
      // First expand the execution to capture full content
      if (!expandedSequences.has(exportingExecution.id)) {
        toggleSequence(exportingExecution.id);
      }
      
      // Expand all steps for capture
      setExportAllStepsExpanded(true);
      
      // Wait for all expansions to render
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Find the execution element
      const element = document.getElementById(`execution-${exportingExecution.id}`);
      if (!element) {
        console.error('Could not find execution element');
        setExporting(false);
        setExportAllStepsExpanded(false);
        return;
      }
      
      // Capture the element as canvas
      const canvas = await html2canvas(element, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowHeight: element.scrollHeight + 500,
        height: element.scrollHeight + 100,
      } as any);
      
      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'mm',
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20; // 10mm margin on each side
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Add title
      pdf.setFontSize(16);
      pdf.setTextColor(30, 58, 95); // Navy color
      pdf.text(`Risk Analysis Report: ${exportingExecution.name}`, 10, 15);
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, 10, 22);
      
      // Add content image
      let yOffset = 30;
      
      if (imgHeight > pageHeight - yOffset - 10) {
        // Multi-page handling
        let remainingHeight = imgHeight;
        let sourceY = 0;
        
        while (remainingHeight > 0) {
          const chunkHeight = Math.min(remainingHeight, pageHeight - yOffset - 10);
          const sourceHeight = (chunkHeight / imgHeight) * canvas.height;
          
          // Create a temporary canvas for this chunk
          const chunkCanvas = document.createElement('canvas');
          chunkCanvas.width = canvas.width;
          chunkCanvas.height = sourceHeight;
          const ctx = chunkCanvas.getContext('2d');
          ctx?.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);
          
          const chunkImgData = chunkCanvas.toDataURL('image/png');
          pdf.addImage(chunkImgData, 'PNG', 10, yOffset, imgWidth, chunkHeight);
          
          remainingHeight -= chunkHeight;
          sourceY += sourceHeight;
          
          if (remainingHeight > 0) {
            pdf.addPage();
            yOffset = 10;
          }
        }
      } else {
        pdf.addImage(imgData, 'PNG', 10, yOffset, imgWidth, imgHeight);
      }
      
      // Save PDF
      pdf.save(`risk-analysis-${exportingExecution.name}-${new Date().toISOString().split('T')[0]}.pdf`);
      event('results_export_pdf', { event_category: 'results', event_action: 'export_pdf' });
      
    } catch (error) {
      console.error('Failed to export PDF:', error);
    } finally {
      setExportAllStepsExpanded(false);
      setExporting(false);
      setExportingExecution(null);
    }
  };

  const handleExportSequenceCsv = async (sequenceId: string, sequenceName: string) => {
    setExporting(true);
    
    try {
      const { data } = await fetchSequenceCsv({
        variables: { sequenceId, timeRange }
      });
      
      if (data?.exportSequenceCsv) {
        // Create and download CSV file
        const blob = new Blob([data.exportSequenceCsv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `risk-analysis-${sequenceName}-all-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Failed to export sequence CSV:', error);
    } finally {
      setExporting(false);
    }
  };
  
  // Convert sequence executions to hierarchical accordion structure
  const accordionData = React.useMemo(() => {
    if (!executionsData?.sequenceExecutions) return {};
    
    const grouped: Record<string, {
      apiConfig: string;
      executions: Array<{
        execution: SequenceExecutionResult;
        overallResult: any;
        stepResults: any[];
      }>;
      totalExecutions: number;
      totalViolations: number;
      totalErrors: number;
      avgRiskScore: number;
    }> = {};
    
    executionsData.sequenceExecutions.forEach((execution: SequenceExecutionResult) => {
      const apiConfigName = execution.sequence.name;
      
      if (!grouped[apiConfigName]) {
        grouped[apiConfigName] = {
          apiConfig: apiConfigName,
          executions: [],
          totalExecutions: 0,
          totalViolations: 0,
          totalErrors: 0,
          avgRiskScore: 0
        };
      }
      
      // Create overall execution result
      const overallResult = {
        id: `exec-${execution.id}`,
        timestamp: execution.startedAt,
        apiConfig: execution.sequence.name,
        sequenceName: execution.sequence.name,
        biasScore: execution.overallBiasScore || 0,
        riskScore: execution.overallRiskScore || 0,
        biasLevel: execution.status === 'failed' ? 'error' : 
                  (execution.overallBiasScore || 0) < 50 ? 'high' :
                  (execution.overallBiasScore || 0) < 70 ? 'medium' : 'low',
        categories: execution.status === 'failed' ? ['Error'] : [],
        confidence: execution.status === 'failed' ? 0 : (execution.overallConfidenceScore || null),
        violation: execution.status === 'failed' ? true : (execution.violationsDetected > 0),
        responseTime: execution.completedAt ? 
          new Date(execution.completedAt).getTime() - new Date(execution.startedAt).getTime() : 0,
        requestData: { sequence: execution.sequence.name, totalSteps: execution.totalSteps },
        responseData: { 
          status: execution.status,
          completedSteps: execution.completedSteps,
          failedSteps: execution.failedSteps
        },
        analysis: execution.errorMessage || 
                 `Sequence executed ${execution.completedSteps}/${execution.totalSteps} steps successfully. ${execution.violationsDetected} violations detected.`,
        errorMessage: execution.errorMessage,
        status: execution.status
      };
      
      // Create step results
      const stepResults = execution.stepExecutions.map(step => {
        // Only show bias/risk data if analysis was actually performed
        const hasValidAnalysis = step.biasAnalysisPerformed && (step.biasScore !== null && step.biasScore !== undefined);
        
        return {
          id: `step-${step.id}`,
          timestamp: step.startedAt,
          apiConfig: `${execution.sequence.name} - Step`,
          sequenceName: execution.sequence.name,
          biasScore: hasValidAnalysis ? step.biasScore : null,
          riskScore: hasValidAnalysis ? step.riskScore : null,
          biasLevel: step.status === 'failed' ? 'error' :
                    !hasValidAnalysis ? 'none' :
                    (step.biasScore || 0) < 50 ? 'high' :
                    (step.biasScore || 0) < 70 ? 'medium' : 'low',
          riskLevel: step.status === 'failed' ? 'error' :
                    !hasValidAnalysis ? 'none' :
                    (step.riskScore || 0) < 50 ? 'high' :
                    (step.riskScore || 0) < 70 ? 'medium' : 'low',
          categories: step.status === 'failed' ? ['Error'] : 
                     !hasValidAnalysis ? [] : 
                     [...(step.biasCategories || []), ...(step.riskCategories || [])],
          confidence: step.status === 'failed' ? 0 : hasValidAnalysis ? (step.confidenceScore || null) : 0,
          violation: step.status === 'failed' ? true : hasValidAnalysis ? (step.biasScore || 0) < 70 : false,
          responseTime: step.responseTimeMs || 0,
          requestData: {
            url: step.resolvedUrl,
            method: step.stepExecutionDetails?.method,
            headers: step.resolvedHeaders,
            body: step.resolvedBody,
            preExecutionVariables: step.preExecutionVariables,
            stepType: step.stepExecutionDetails?.step_type,
            stepName: step.stepExecutionDetails?.step_name
          },
          responseData: {
            statusCode: step.responseStatusCode,
            headers: step.responseHeaders,
            body: step.responseBody,
            responseTime: step.responseTimeMs,
            postExecutionVariables: step.postExecutionVariables,
            extractedVariables: step.extractedVariables
          },
          analysis: step.errorMessage || step.biasAnalysisDetails || 'Step executed successfully',
          errorMessage: step.errorMessage,
          status: step.status,
          stepExecution: step,
          detailedData: {
            variableAssignments: step.variableAssignmentsPerformed || [],
            ifConditions: step.ifConditionResults || [],
            loopIterations: step.loopIterationsData || [],
            llmRequests: step.llmRequestsMade || [],
            executionDetails: step.stepExecutionDetails || {},
            skipCondition: step.skipConditionResult || {}
          }
        };
      });
      
      grouped[apiConfigName].executions.push({
        execution,
        overallResult,
        stepResults
      });
      
      // Update group statistics
      grouped[apiConfigName].totalExecutions++;
      if (overallResult.violation) grouped[apiConfigName].totalViolations++;
      if (overallResult.status === 'failed') grouped[apiConfigName].totalErrors++;
    });
    
    // Calculate average risk scores for each group
    Object.values(grouped).forEach(group => {
      const validScores = group.executions
        .filter(e => e.overallResult.status !== 'failed')
        .map(e => e.overallResult.riskScore);
      group.avgRiskScore = validScores.length > 0 
        ? validScores.reduce((sum, score) => sum + score, 0) / validScores.length 
        : 0;
    });
    
    return grouped;
  }, [executionsData]);
  
  // Calculate summary statistics from executions data
  const summaryStats = React.useMemo(() => {
    if (!executionsData?.sequenceExecutions) {
      return {
        totalExecutions: 0,
        totalSteps: 0,
        totalViolations: 0,
        totalErrors: 0,
        // New harm category fields
        totalHarmCategoryPasses: 0,
        totalHarmCategoryFailures: 0,
        avgHarmScore: 0,
        overallIsSafe: true,
        // Legacy fields for backward compatibility
        avgBiasScore: 0,
        avgRiskScore: 0
      };
    }

    const executions = executionsData.sequenceExecutions;
    let totalSteps = 0;
    let totalViolations = 0;
    let totalErrors = 0;
    let totalHarmPasses = 0;
    let totalHarmFailures = 0;
    let harmScoreSum = 0;
    let validHarmCount = 0;
    let biasScoreSum = 0;
    let riskScoreSum = 0;
    let validBiasCount = 0;
    let validRiskCount = 0;

    executions.forEach((exec: SequenceExecutionResult) => {
      // Count total steps across all executions
      totalSteps += exec.stepExecutions.length;
      
      // Count harm category data
      totalHarmPasses += exec.totalHarmCategoryPasses;
      totalHarmFailures += exec.totalHarmCategoryFailures;
      
      if (exec.overallSafetyScore !== null && exec.overallSafetyScore !== undefined) {
        harmScoreSum += exec.overallSafetyScore;
        validHarmCount++;
      }
      
      // Legacy bias/risk analysis
      exec.stepExecutions.forEach(step => {
        if (step.biasAnalysisPerformed) {
          if (step.biasScore !== null && step.biasScore !== undefined) {
            biasScoreSum += step.biasScore;
            validBiasCount++;
          }
          if (step.riskScore !== null && step.riskScore !== undefined) {
            riskScoreSum += step.riskScore;
            validRiskCount++;
          }
        }
      });
      
      // Count errors
      if (exec.status === 'failed') totalErrors++;
    });

    const totalCategories = totalHarmPasses + totalHarmFailures;
    const avgHarmScore = totalCategories > 0 ? (totalHarmPasses / totalCategories) * 100 : 100;
    
    // Violations = total harm category failures
    totalViolations = totalHarmFailures;

    return {
      totalExecutions: executions.length,
      totalSteps,
      totalViolations,
      totalErrors,
      // New harm category fields
      totalHarmCategoryPasses: totalHarmPasses,
      totalHarmCategoryFailures: totalHarmFailures,
      avgHarmScore,
      overallIsSafe: totalHarmFailures === 0,
      // Legacy fields for backward compatibility
      avgBiasScore: validBiasCount > 0 ? biasScoreSum / validBiasCount : 0,
      avgRiskScore: validRiskCount > 0 ? riskScoreSum / validRiskCount : 0
    };
  }, [executionsData]);
  
  // Flatten for detail views (backward compatibility)
  const results = React.useMemo(() => {
    return Object.values(accordionData).flatMap(group => 
      group.executions.flatMap(exec => [exec.overallResult, ...exec.stepResults])
    );
  }, [accordionData]);
  
  const [filteredResults, setFilteredResults] = useState(results);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [expandedApiConfigs, setExpandedApiConfigs] = useState<Set<string>>(new Set());
  const [expandedSequences, setExpandedSequences] = useState<Set<string>>(new Set());
  const [exportAllStepsExpanded, setExportAllStepsExpanded] = useState(false); // For PDF export
  
  // Update filtered results when results change
  React.useEffect(() => {
    setFilteredResults(results);
  }, [results]);
  
  // Auto-expand accordion for execution from URL parameter
  useEffect(() => {
    if (executionIdFromUrl && accordionData) {
      // Find which API config contains this execution
      for (const [apiConfigName, groupData] of Object.entries(accordionData)) {
        const hasExecution = groupData.executions.some(
          (exec: any) => exec.execution.id === executionIdFromUrl
        );
        if (hasExecution) {
          setExpandedApiConfigs(new Set([apiConfigName]));
          // Scroll to the element after a short delay to ensure it's rendered
          setTimeout(() => {
            const element = document.getElementById(`execution-${executionIdFromUrl}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 300);
          break;
        }
      }
    }
  }, [executionIdFromUrl, accordionData]);

  const getBiasScoreColor = (score: number | null, status?: string) => {
    if (status === 'failed') return 'error';
    if (status === 'running') return 'secondary'; // Purple for running state
    if (score === null || score === undefined) return 'default';
    if (score >= 70) return 'success';
    if (score >= 50) return 'warning';
    return 'error';
  };

  const getRiskScoreColor = (score: number | null, status?: string) => {
    if (status === 'failed') return 'error';
    if (status === 'running') return 'secondary'; // Purple for running state
    if (score === null || score === undefined) return 'default';
    if (score >= 70) return 'success';
    if (score >= 50) return 'warning';
    return 'error';
  };

  const getBiasLevelColor = (level: string) => {
    switch (level) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'error': return 'error';
      case 'none': return 'default';
      default: return 'default';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };
  
  const formatBiasScore = (score: number | null, status?: string) => {
    if (status === 'failed') return 'ERROR';
    if (status === 'running') return 'Running...';
    if (score === null || score === undefined) return 'N/A';
    return `${score.toFixed(1)}%`;
  };

  const formatRiskScore = (score: number | null, status?: string) => {
    if (status === 'failed') return 'ERROR';
    if (status === 'running') return 'Running...';
    if (score === null || score === undefined) return 'N/A';
    return `${score.toFixed(1)}%`;
  };

  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'error': return 'error';
      case 'none': return 'default';
      default: return 'default';
    }
  };

  const handleRowClick = (id: string, stepStatus?: string) => {
    // Don't allow clicking on running steps
    if (stepStatus === 'running') {
      return;
    }
    setExpandedRow(expandedRow === id ? null : id);
  };

  const toggleApiConfig = (apiConfigName: string) => {
    const newExpanded = new Set(expandedApiConfigs);
    if (newExpanded.has(apiConfigName)) {
      newExpanded.delete(apiConfigName);
    } else {
      newExpanded.add(apiConfigName);
    }
    setExpandedApiConfigs(newExpanded);
  };

  const toggleSequence = (sequenceId: string) => {
    const newExpanded = new Set(expandedSequences);
    if (newExpanded.has(sequenceId)) {
      newExpanded.delete(sequenceId);
    } else {
      newExpanded.add(sequenceId);
    }
    setExpandedSequences(newExpanded);
  };

  return (
    <Box sx={{ bgcolor: '#f0fdfa', minHeight: '100vh', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h2" sx={{ fontWeight: 'bold', color: '#1f2937' }}>
          Sequence Executions
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={(e) => setTimeRange(e.target.value)}
              sx={{ bgcolor: 'white' }}
            >
              <MenuItem value="30_min">Last 30 Minutes</MenuItem>
              <MenuItem value="1_hour">Last Hour</MenuItem>
              <MenuItem value="24_hours">Last 24 Hours</MenuItem>
              <MenuItem value="1_week">Last Week</MenuItem>
              <MenuItem value="30_days">Last 30 Days</MenuItem>
              <MenuItem value="all_time">All Time</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            data-ga-label="BiasResults Refresh"
            startIcon={<Refresh />}
            onClick={() => refetch()}
            disabled={executionsLoading}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      
      {executionsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading results: {executionsError.message}
          {executionsError.graphQLErrors && executionsError.graphQLErrors.length > 0 && (
            <Box sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}>
              <strong>GraphQL Errors:</strong>
              {executionsError.graphQLErrors.map((err: any, idx: number) => (
                <div key={idx}>{JSON.stringify(err, null, 2)}</div>
              ))}
            </Box>
          )}
          {executionsError.networkError && (
            <Box sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.875rem' }}>
              <strong>Network Error:</strong> {executionsError.networkError.message}
            </Box>
          )}
        </Alert>
      )}
      
      {executionsLoading && results.length === 0 && (
        <LinearProgress sx={{ mb: 2 }} />
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Total Executions
              </Typography>
              <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
                {totalExecutions}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {summaryStats.totalSteps} steps on this page
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Errors
              </Typography>
              <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
                {summaryStats.totalErrors}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                failed executions
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #0891b2 0%, #22d3ee 100%)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Violations
              </Typography>
              <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
                {summaryStats.totalViolations}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                steps flagged
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ 
            height: '100%',
            background: summaryStats.avgHarmScore >= 90
              ? 'linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%)'
              : summaryStats.avgHarmScore >= 75
              ? 'linear-gradient(135deg, #0891b2 0%, #22d3ee 100%)'
              : 'linear-gradient(135deg, #f43f5e 0%, #fb7185 100%)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Safety Score
              </Typography>
              <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
                {summaryStats.avgHarmScore > 0 ? summaryStats.avgHarmScore.toFixed(1) : 'N/A'}%
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {summaryStats.totalHarmCategoryPasses} passes, {summaryStats.totalHarmCategoryFailures} failures
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Accordion Results */}
      {Object.keys(accordionData).length === 0 && !executionsLoading ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="h6">
            No bias detection results yet. Try running some sequences to see results here.
          </Typography>
        </Paper>
      ) : (
        <Box>
          {Object.values(accordionData).map((group) => (
            <Card key={group.apiConfig} sx={{ mb: 2 }}>
              {/* API Configuration Header */}
              <CardContent
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { bgcolor: 'action.hover' },
                  borderBottom: expandedApiConfigs.has(group.apiConfig) ? '1px solid' : 'none',
                  borderColor: 'divider'
                }}
                onClick={() => toggleApiConfig(group.apiConfig)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton size="small">
                      {expandedApiConfigs.has(group.apiConfig) ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                    </IconButton>
                    <Settings color="primary" />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {group.apiConfig}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        API Configuration • {group.totalExecutions} execution{group.totalExecutions !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Box>
                  <Tooltip title="Export all executions as CSV">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<TableChart />}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Get sequence ID from first execution
                        const firstExec = group.executions[0];
                        if (firstExec) {
                          handleExportSequenceCsv(firstExec.execution.sequence.id, group.apiConfig);
                        }
                      }}
                      disabled={exporting || group.executions.length === 0}
                      sx={{ 
                        mr: 1,
                        textTransform: 'none',
                      }}
                    >
                      Export All CSV
                    </Button>
                  </Tooltip>
                </Box>
              </CardContent>

              {/* Sequence Executions */}
              <Collapse in={expandedApiConfigs.has(group.apiConfig)} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2, pb: 2, maxHeight: '70vh', overflowY: 'auto' }}>
                  {group.executions
                    .slice(0, getVisibleCount(group.apiConfig))
                    .map((execData) => (
                    <Card 
                      key={execData.execution.id} 
                      id={`execution-${execData.execution.id}`}
                      variant="outlined" 
                      sx={{ 
                        mb: 2, 
                        ml: 4,
                        ...(executionIdFromUrl === execData.execution.id && {
                          border: '2px solid #1e3a5f',
                          boxShadow: '0 0 10px rgba(13, 148, 136, 0.3)'
                        })
                      }}
                    >
                      {/* Sequence Execution Header */}
                      <CardContent
                        sx={{ 
                          cursor: 'pointer', 
                          '&:hover': { bgcolor: 'action.hover' },
                          borderBottom: expandedSequences.has(execData.execution.id) ? '1px solid' : 'none',
                          borderColor: 'divider',
                          py: 2
                        }}
                        onClick={() => toggleSequence(execData.execution.id)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <IconButton size="small">
                              {expandedSequences.has(execData.execution.id) ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                            </IconButton>
                            <PlayArrow color={execData.overallResult.status === 'completed' ? 'success' : 
                                           execData.overallResult.status === 'running' ? 'secondary' : 'error'} />
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                Execution {formatTimestamp(execData.overallResult.timestamp)}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Chip
                                  label={execData.overallResult.status}
                                  color={execData.overallResult.status === 'completed' ? 'success' : 
                                         execData.overallResult.status === 'running' ? 'secondary' : 'error'}
                                  size="small"
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {execData.overallResult.responseData.completedSteps}/{execData.overallResult.requestData.totalSteps} steps • {formatDuration(execData.overallResult.responseTime)}
                                </Typography>
                                {execData.overallResult.errorMessage && (
                                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5, fontWeight: 'bold' }}>
                                    ⚠️ {execData.overallResult.errorMessage}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            
                            {/* Risk Score */}
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography
                                variant="h6"
                                sx={{ 
                                  fontWeight: 'bold', 
                                  color: `${getRiskScoreColor(execData.overallResult.riskScore, execData.overallResult.status)}.main` 
                                }}
                              >
                                {formatRiskScore(execData.overallResult.riskScore, execData.overallResult.status)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Risk Score
                              </Typography>
                            </Box>
                            <Chip
                              label={execData.overallResult.violation ? 'Violation' : 'Clean'}
                              color={execData.overallResult.violation ? 'error' : 'success'}
                              size="small"
                            />
                            <Tooltip title="Export results">
                              <IconButton
                                size="small"
                                onClick={(e) => handleExportClick(
                                  e, 
                                  execData.execution.id,
                                  execData.execution.sequence.id,
                                  group.apiConfig
                                )}
                                disabled={exporting}
                                sx={{ 
                                  color: 'text.secondary',
                                  '&:hover': { color: 'primary.main', bgcolor: 'primary.lighter' }
                                }}
                              >
                                <Download fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete execution">
                              <IconButton
                                data-ga-label="Delete execution"
                                size="small"
                                onClick={(e) => handleDeleteClick(
                                  e, 
                                  execData.execution.id, 
                                  group.apiConfig,
                                  execData.overallResult.timestamp
                                )}
                                sx={{ 
                                  color: 'text.secondary',
                                  '&:hover': { color: 'error.main', bgcolor: 'error.lighter' }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        
                      </CardContent>

                      {/* Individual Steps */}
                      <Collapse in={expandedSequences.has(execData.execution.id)} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                          {execData.stepResults.length === 0 ? (
                            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                              No step details available
                            </Typography>
                          ) : (
                            execData.stepResults.map((step: any) => (
                              <Card key={step.id} variant="outlined" sx={{ mb: 1, ml: 2 }}>
                                <CardContent
                                  sx={{ 
                                    cursor: step.status === 'running' ? 'not-allowed' : 'pointer', 
                                    '&:hover': step.status === 'running' ? {} : { bgcolor: 'action.hover' },
                                    py: 1.5
                                  }}
                                  onClick={() => handleRowClick(step.id, step.status)}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                      {step.status === 'completed' ? (
                                        <CheckCircle color="success" sx={{ fontSize: 20 }} />
                                      ) : step.status === 'running' ? (
                                        <PlayArrow color="secondary" sx={{ fontSize: 20 }} />
                                      ) : (
                                        <Cancel color="error" sx={{ fontSize: 20 }} />
                                      )}
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                          {step.requestData.stepName || step.requestData.stepType || 'Step'} • {formatTimestamp(step.timestamp)}
                                        </Typography>
                                        {step.requestData.url && (
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            {step.requestData.method} {step.requestData.url}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                                      {step.stepExecution?.biasAnalysisPerformed && (
                                        <>
                                          <Typography
                                            variant="body2"
                                            sx={{ 
                                              fontWeight: 'bold', 
                                              color: `${getRiskScoreColor(step.riskScore, step.status)}.main` 
                                            }}
                                          >
                                            {formatRiskScore(step.riskScore, step.status)}
                                          </Typography>
                                          <Chip
                                            label={step.riskLevel === 'none' ? 'n/a' : step.riskLevel}
                                            color={getRiskLevelColor(step.riskLevel) as any}
                                            size="small"
                                          />
                                        </>
                                      )}
                                      <Typography variant="caption" color="text.secondary">
                                        {step.responseTime}ms
                                      </Typography>
                                    </Box>
                                  </Box>
                                </CardContent>

                                {/* Step Details Collapse */}
                                <Collapse in={expandedRow === step.id || exportAllStepsExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="subtitle2" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                                      🔍 Step Execution Details
                                    </Typography>
                                    
                                    {/* Basic Request/Response Section */}
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                      <Grid item xs={12} md={6}>
                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                          📤 Request Details
                                        </Typography>
                                        <Paper sx={{ p: 1.5, bgcolor: 'white', border: '1px solid #e0e0e0' }}>
                                          {step.requestData.stepType && (
                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                              <strong>Step Type:</strong> <Chip label={step.requestData.stepType} size="small" />
                                            </Typography>
                                          )}
                                          {step.requestData.url && (
                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                              <strong>URL:</strong> {step.requestData.url}
                                            </Typography>
                                          )}
                                          {step.requestData.method && (
                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                              <strong>Method:</strong> <Chip label={step.requestData.method} size="small" color="primary" />
                                            </Typography>
                                          )}
                                        </Paper>
                                      </Grid>
                                      
                                      <Grid item xs={12} md={6}>
                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                                          📥 Response Details
                                        </Typography>
                                        <Paper sx={{ p: 1.5, bgcolor: 'white', border: '1px solid #e0e0e0' }}>
                                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                            <strong>Status:</strong> <Chip 
                                              label={step.responseData.statusCode || 'N/A'} 
                                              size="small" 
                                              color={step.responseData.statusCode === 200 ? 'success' : 'error'} 
                                            />
                                          </Typography>
                                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                                            <strong>Response Time:</strong> {step.responseData.responseTime || 0}ms
                                          </Typography>
                                          {step.responseData.body && (
                                            <Box sx={{ mt: 1 }}>
                                              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                                                Response Body:
                                              </Typography>
                                              <Paper sx={{ p: 1, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0', maxHeight: '200px', overflow: 'auto' }}>
                                                <Typography 
                                                  variant="caption" 
                                                  sx={{ 
                                                    display: 'block', 
                                                    fontFamily: 'monospace', 
                                                    fontSize: '0.7rem',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word'
                                                  }}
                                                >
                                                  {step.responseData.body}
                                                </Typography>
                                              </Paper>
                                            </Box>
                                          )}
                                        </Paper>
                                      </Grid>
                                    </Grid>

                                    {/* Error Message - Show if step failed */}
                                    {step.errorMessage && (
                                      <Box sx={{ mb: 2 }}>
                                        <Paper sx={{ p: 2, bgcolor: '#ffebee', border: '2px solid #ef5350', borderRadius: 1 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                              ⚠️ ERROR
                                            </Typography>
                                          </Box>
                                          <Typography variant="body2" sx={{ color: 'error.dark', fontFamily: 'monospace' }}>
                                            {step.errorMessage}
                                          </Typography>
                                        </Paper>
                                      </Box>
                                    )}

                                    {/* Variables Section */}
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                      <Grid item xs={12} md={4}>
                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', color: 'info.main' }}>
                                          🔧 Variables (Before)
                                        </Typography>
                                        <Paper sx={{ p: 1, bgcolor: 'white', border: '1px solid #e0e0e0', maxHeight: '150px', overflow: 'auto' }}>
                                          <pre style={{ fontSize: '0.65rem', margin: 0 }}>
                                            {JSON.stringify(step.requestData.preExecutionVariables || {}, null, 2)}
                                          </pre>
                                        </Paper>
                                      </Grid>
                                      <Grid item xs={12} md={4}>
                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                          ✅ Extracted Variables
                                        </Typography>
                                        <Paper sx={{ p: 1, bgcolor: 'white', border: '1px solid #e0e0e0', maxHeight: '150px', overflow: 'auto' }}>
                                          <pre style={{ fontSize: '0.65rem', margin: 0 }}>
                                            {JSON.stringify(step.responseData.extractedVariables || {}, null, 2)}
                                          </pre>
                                        </Paper>
                                      </Grid>
                                      <Grid item xs={12} md={4}>
                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                                          🔧 Variables (After)
                                        </Typography>
                                        <Paper sx={{ p: 1, bgcolor: 'white', border: '1px solid #e0e0e0', maxHeight: '150px', overflow: 'auto' }}>
                                          <pre style={{ fontSize: '0.65rem', margin: 0 }}>
                                            {JSON.stringify(step.responseData.postExecutionVariables || {}, null, 2)}
                                          </pre>
                                        </Paper>
                                      </Grid>
                                    </Grid>

                                    {/* AI Analysis - Only show if harm analysis was performed */}
                                    {step.stepExecution?.harmAnalysisPerformed && (
                                      <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                          🤖 AI Risk Analysis
                                        </Typography>
                                        <Paper sx={{ p: 1.5, bgcolor: 'white', border: '1px solid #e0e0e0' }}>
                                          <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 'bold' }}>
                                            {step.stepExecution.isSafe ? '✅ All 13 harm categories passed' : `⚠️ ${step.stepExecution.harmCategoryFailures} of 13 categories failed, ${step.stepExecution.harmCategoryPasses} passed`}
                                          </Typography>
                                          
                                          {/* Failed Categories */}
                                          {step.stepExecution.failedCategoryNames && step.stepExecution.failedCategoryNames.length > 0 && (
                                            <Box sx={{ mt: 1.5, mb: 1 }}>
                                              <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'error.main', display: 'block', mb: 0.5 }}>
                                                ❌ FAILED ({step.stepExecution.failedCategoryNames.length} of 13):
                                              </Typography>
                                              <Box sx={{ pl: 1 }}>
                                                {step.stepExecution.failedCategoryNames.map((category: string, idx: number) => (
                                                  <Typography key={idx} variant="caption" sx={{ display: 'block', color: 'error.main' }}>
                                                    • {category}
                                                  </Typography>
                                                ))}
                                              </Box>
                                            </Box>
                                          )}
                                          
                                          {/* Passed Categories */}
                                          {step.stepExecution.passedCategoryNames && step.stepExecution.passedCategoryNames.length > 0 && (
                                            <Box sx={{ mt: 1.5, mb: 1 }}>
                                              <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'success.main', display: 'block', mb: 0.5 }}>
                                                ✅ PASSED ({step.stepExecution.passedCategoryNames.length} of 13):
                                              </Typography>
                                              <Box sx={{ pl: 1 }}>
                                                {step.stepExecution.passedCategoryNames.map((category: string, idx: number) => (
                                                  <Typography key={idx} variant="caption" sx={{ display: 'block', color: 'success.main' }}>
                                                    • {category}
                                                  </Typography>
                                                ))}
                                              </Box>
                                            </Box>
                                          )}
                                          
                                          {/* Ignored Categories */}
                                          {step.stepExecution.ignoredCategoryNames && step.stepExecution.ignoredCategoryNames.length > 0 && (
                                            <Box sx={{ mt: 1.5, mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                              <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                                🔕 IGNORED ({step.stepExecution.ignoredCategoryNames.length} of 13):
                                              </Typography>
                                              <Box sx={{ pl: 1 }}>
                                                {step.stepExecution.ignoredCategoryNames.map((category: string, idx: number) => (
                                                  <Typography key={idx} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                                    • {category}
                                                  </Typography>
                                                ))}
                                              </Box>
                                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
                                                These categories were excluded from analysis based on sequence settings.
                                              </Typography>
                                            </Box>
                                          )}
                                          
                                          {step.errorMessage && (
                                            <Box sx={{ mt: 1, p: 1, bgcolor: '#ffebee', borderRadius: 1 }}>
                                              <Typography variant="caption" color="error">
                                                <strong>Error:</strong> {step.errorMessage}
                                              </Typography>
                                            </Box>
                                          )}
                                          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                                            <Typography variant="caption" color="text.secondary">
                                              General Safety & Policy Monitoring
                                            </Typography>
                                          </Box>
                                        </Paper>
                                      </Box>
                                    )}

                                    {/* Regulatory Compliance Analysis */}
                                    {step.stepExecution?.graniteAnalysisPerformed && (
                                      <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                          Standards & Regulatory Compliance Analysis
                                        </Typography>
                                        <Paper sx={{ p: 1.5, bgcolor: 'white', border: '1px solid #1976d2' }}>
                                          <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 'bold' }}>
                                            {!step.stepExecution.graniteRiskDetected 
                                              ? `✅ All ${step.stepExecution.graniteCategoriesChecked} risk categories passed` 
                                              : `⚠️ ${step.stepExecution.graniteCategoriesFailed} of ${step.stepExecution.graniteCategoriesChecked} categories flagged risk`}
                                          </Typography>
                                          
                                          {/* Risk Category Results */}
                                          <Grid container spacing={1} sx={{ mt: 1 }}>
                                            {/* Risk Detection */}
                                            <Grid item xs={12} md={6}>
                                              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: '#1976d2' }}>
                                                Risk Detection:
                                              </Typography>
                                              <Box sx={{ pl: 1 }}>
                                                {[
                                                  { key: 'graniteHarm', label: 'Harmful Content', value: step.stepExecution.graniteHarm },
                                                  { key: 'graniteSocialBias', label: 'Social Bias', value: step.stepExecution.graniteSocialBias },
                                                  { key: 'graniteJailbreak', label: 'Jailbreak Attempt', value: step.stepExecution.graniteJailbreak },
                                                  { key: 'graniteViolence', label: 'Violence', value: step.stepExecution.graniteViolence },
                                                  { key: 'graniteProfanity', label: 'Profanity', value: step.stepExecution.graniteProfanity },
                                                  { key: 'graniteSexualContent', label: 'Sexual Content', value: step.stepExecution.graniteSexualContent },
                                                  { key: 'graniteUnethicalBehavior', label: 'Unethical Behavior', value: step.stepExecution.graniteUnethicalBehavior },
                                                ].map((cat) => (
                                                  <Typography 
                                                    key={cat.key} 
                                                    variant="caption" 
                                                    sx={{ 
                                                      display: 'block', 
                                                      color: cat.value === true ? 'error.main' : cat.value === false ? 'success.main' : 'text.secondary' 
                                                    }}
                                                  >
                                                    {cat.value === true ? '❌' : cat.value === false ? '✅' : '⏺️'} {cat.label}
                                                    {cat.value === null && ' (not checked)'}
                                                  </Typography>
                                                ))}
                                              </Box>
                                            </Grid>
                                            
                                            {/* RAG Quality Assessment */}
                                            <Grid item xs={12} md={6}>
                                              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5, color: '#1976d2' }}>
                                                RAG Quality Assessment:
                                              </Typography>
                                              <Box sx={{ pl: 1 }}>
                                                {(() => {
                                                  // Map category keys to explanation keys
                                                  const explanationKeyMap: Record<string, string> = {
                                                    'graniteRelevance': 'relevance',
                                                    'graniteGroundedness': 'groundedness',
                                                    'graniteAnswerRelevance': 'answer_relevance',
                                                  };
                                                  const explanations = step.stepExecution.graniteRagExplanations || {};
                                                  
                                                  return [
                                                    { key: 'graniteRelevance', label: 'Context Relevance', value: step.stepExecution.graniteRelevance },
                                                    { key: 'graniteGroundedness', label: 'Response Grounded', value: step.stepExecution.graniteGroundedness },
                                                    { key: 'graniteAnswerRelevance', label: 'Answer Relevance', value: step.stepExecution.graniteAnswerRelevance },
                                                  ].map((cat) => {
                                                    const hasIssue = cat.value === true;
                                                    const noIssue = cat.value === false;
                                                    const explanationKey = explanationKeyMap[cat.key];
                                                    const explanation = explanations[explanationKey] as RagExplanation | undefined;
                                                    
                                                    return (
                                                      <RagExplanationTooltip
                                                        key={cat.key}
                                                        label={cat.label}
                                                        hasIssue={hasIssue}
                                                        noIssue={noIssue}
                                                        isNull={cat.value === null}
                                                        explanation={explanation || null}
                                                      />
                                                    );
                                                  });
                                                })()}
                                              </Box>
                                              {step.stepExecution.graniteRagIssuesDetected && (
                                                <Box sx={{ mt: 1, p: 1, bgcolor: '#fff3e0', borderRadius: 1 }}>
                                                  <Typography variant="caption" color="warning.dark">
                                                    ⚠️ RAG quality issues detected
                                                  </Typography>
                                                </Box>
                                              )}
                                            </Grid>
                                          </Grid>
                                          
                                          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                                            <Typography variant="caption" color="text.secondary">
                                              Standards & Regulatory Compliance | 
                                              Score: {step.stepExecution.graniteOverallScore?.toFixed(1) || 'N/A'}%
                                            </Typography>
                                          </Box>
                                          
                                        </Paper>
                                      </Box>
                                    )}

                                    {/* User Perception & Experience Analysis */}
                                    {(step.stepExecution?.llmSemanticAnalysisPerformed || step.stepExecution?.embeddingAnalysisPerformed || step.stepExecution?.embeddingGroundednessScore !== null) && (
                                      <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                                          User Perception & Experience Analysis
                                        </Typography>
                                        <Paper sx={{ p: 1.5, bgcolor: 'white', border: '1px solid #9c27b0' }}>
                                          {/* Summary Header */}
                                          <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 'bold' }}>
                                            {(() => {
                                              // Use LLM results if available, otherwise fall back to embeddings
                                              const useLlm = step.stepExecution?.llmSemanticAnalysisPerformed;
                                              const allPassed = useLlm 
                                                ? step.stepExecution?.llmSemanticAllPassed 
                                                : step.stepExecution?.embeddingAllPassed;
                                              const issueCount = useLlm
                                                ? (step.stepExecution?.llmSemanticIssuesSummary ? 1 : 0)
                                                : (step.stepExecution?.embeddingIssuesDetected?.length || 0);
                                              return allPassed 
                                                ? '✅ All semantic checks passed' 
                                                : `⚠️ ${issueCount} issue(s) detected`;
                                            })()}
                                          </Typography>
                                          
                                          {/* LLM-based Analysis Results (Primary) - 2x2 Grid Layout */}
                                          {step.stepExecution?.llmSemanticAnalysisPerformed && (
                                            <Box sx={{ 
                                              display: 'grid', 
                                              gridTemplateColumns: 'repeat(2, 1fr)', 
                                              gap: 2, 
                                              mt: 1.5 
                                            }}>
                                              {/* Grounded - Top Left */}
                                              <Box sx={{ 
                                                p: 1.5, 
                                                borderRadius: 2,
                                                bgcolor: step.stepExecution.llmGroundednessPassed !== false ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                                                border: `1px solid ${step.stepExecution.llmGroundednessPassed !== false ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                                              }}>
                                                <Typography variant="caption" sx={{ 
                                                  px: 1.5, py: 0.5, borderRadius: 1,
                                                  bgcolor: step.stepExecution.llmGroundednessPassed !== false ? '#dcfce7' : '#fee2e2',
                                                  fontWeight: 'bold',
                                                  display: 'inline-block',
                                                }}>
                                                  {step.stepExecution.llmGroundednessPassed !== false ? '✅' : '❌'} Grounded
                                                </Typography>
                                                {step.stepExecution.llmGroundednessPassed === false && (
                                                  <Typography variant="caption" sx={{ color: '#dc2626', fontStyle: 'italic', mt: 1, display: 'block' }}>
                                                    {step.stepExecution.llmGroundednessExplanation || 'Response was not grounded in the provided context'}
                                                  </Typography>
                                                )}
                                              </Box>
                                              
                                              {/* No Hallucination - Top Right */}
                                              <Box sx={{ 
                                                p: 1.5, 
                                                borderRadius: 2,
                                                bgcolor: step.stepExecution.llmHallucinationPassed !== false ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                                                border: `1px solid ${step.stepExecution.llmHallucinationPassed !== false ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                                              }}>
                                                <Typography variant="caption" sx={{ 
                                                  px: 1.5, py: 0.5, borderRadius: 1,
                                                  bgcolor: step.stepExecution.llmHallucinationPassed !== false ? '#dcfce7' : '#fee2e2',
                                                  fontWeight: 'bold',
                                                  display: 'inline-block',
                                                }}>
                                                  {step.stepExecution.llmHallucinationPassed !== false ? '✅' : '❌'} No Hallucination
                                                </Typography>
                                                {step.stepExecution.llmHallucinationPassed === false && (
                                                  <Typography variant="caption" sx={{ color: '#dc2626', fontStyle: 'italic', mt: 1, display: 'block' }}>
                                                    {step.stepExecution.llmHallucinationExplanation || 'Response contained hallucinated content'}
                                                  </Typography>
                                                )}
                                              </Box>
                                              
                                              {/* Appropriate Tone - Bottom Left */}
                                              <Box sx={{ 
                                                p: 1.5, 
                                                borderRadius: 2,
                                                bgcolor: step.stepExecution.llmTonePassed !== false ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                                                border: `1px solid ${step.stepExecution.llmTonePassed !== false ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                                              }}>
                                                <Typography variant="caption" sx={{ 
                                                  px: 1.5, py: 0.5, borderRadius: 1,
                                                  bgcolor: step.stepExecution.llmTonePassed !== false ? '#dcfce7' : '#fee2e2',
                                                  fontWeight: 'bold',
                                                  display: 'inline-block',
                                                }}>
                                                  {step.stepExecution.llmTonePassed !== false ? '✅' : '❌'} Appropriate Tone
                                                </Typography>
                                                {step.stepExecution.llmTonePassed === false && (
                                                  <Typography variant="caption" sx={{ color: '#dc2626', fontStyle: 'italic', mt: 1, display: 'block' }}>
                                                    {step.stepExecution.llmToneExplanation || 'Response tone was inappropriate'}
                                                  </Typography>
                                                )}
                                              </Box>
                                              
                                              {/* Unbiased - Bottom Right */}
                                              <Box sx={{ 
                                                p: 1.5, 
                                                borderRadius: 2,
                                                bgcolor: step.stepExecution.embeddingBiasPassed !== false ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                                                border: `1px solid ${step.stepExecution.embeddingBiasPassed !== false ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                                              }}>
                                                <Typography variant="caption" sx={{ 
                                                  px: 1.5, py: 0.5, borderRadius: 1,
                                                  bgcolor: step.stepExecution.embeddingBiasPassed !== false ? '#dcfce7' : '#fee2e2',
                                                  fontWeight: 'bold',
                                                  display: 'inline-block',
                                                }}>
                                                  {step.stepExecution.embeddingBiasPassed !== false ? '✅' : '❌'} Unbiased
                                                </Typography>
                                                {step.stepExecution.embeddingBiasPassed === false && (
                                                  <Typography variant="caption" sx={{ color: '#dc2626', fontStyle: 'italic', mt: 1, display: 'block' }}>
                                                    {step.stepExecution.embeddingBiasExplanation || 'Response contained biased content'}
                                                  </Typography>
                                                )}
                                              </Box>
                                            </Box>
                                          )}
                                          
                                          {/* Fallback: Embedding-based Results (if LLM not performed) - 2x2 Grid Layout */}
                                          {!step.stepExecution?.llmSemanticAnalysisPerformed && (
                                            <Box sx={{ 
                                              display: 'grid', 
                                              gridTemplateColumns: 'repeat(2, 1fr)', 
                                              gap: 2, 
                                              mt: 1.5 
                                            }}>
                                              {/* Grounded - Top Left */}
                                              <Box sx={{ 
                                                p: 1.5, 
                                                borderRadius: 2,
                                                bgcolor: step.stepExecution.embeddingGroundednessPassed !== false ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                                                border: `1px solid ${step.stepExecution.embeddingGroundednessPassed !== false ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                                              }}>
                                                <Typography variant="caption" sx={{ 
                                                  px: 1.5, py: 0.5, borderRadius: 1,
                                                  bgcolor: step.stepExecution.embeddingGroundednessPassed !== false ? '#dcfce7' : '#fee2e2',
                                                  fontWeight: 'bold',
                                                  display: 'inline-block',
                                                }}>
                                                  {step.stepExecution.embeddingGroundednessPassed !== false ? '✅' : '❌'} Grounded
                                                </Typography>
                                              </Box>
                                              
                                              {/* No Hallucination - Top Right */}
                                              <Box sx={{ 
                                                p: 1.5, 
                                                borderRadius: 2,
                                                bgcolor: step.stepExecution.embeddingHallucinationPassed !== false ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                                                border: `1px solid ${step.stepExecution.embeddingHallucinationPassed !== false ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                                              }}>
                                                <Typography variant="caption" sx={{ 
                                                  px: 1.5, py: 0.5, borderRadius: 1,
                                                  bgcolor: step.stepExecution.embeddingHallucinationPassed !== false ? '#dcfce7' : '#fee2e2',
                                                  fontWeight: 'bold',
                                                  display: 'inline-block',
                                                }}>
                                                  {step.stepExecution.embeddingHallucinationPassed !== false ? '✅' : '❌'} No Hallucination
                                                </Typography>
                                              </Box>
                                              
                                              {/* Appropriate Tone - Bottom Left */}
                                              <Box sx={{ 
                                                p: 1.5, 
                                                borderRadius: 2,
                                                bgcolor: step.stepExecution.embeddingTonePassed !== false ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                                                border: `1px solid ${step.stepExecution.embeddingTonePassed !== false ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                                              }}>
                                                <Typography variant="caption" sx={{ 
                                                  px: 1.5, py: 0.5, borderRadius: 1,
                                                  bgcolor: step.stepExecution.embeddingTonePassed !== false ? '#dcfce7' : '#fee2e2',
                                                  fontWeight: 'bold',
                                                  display: 'inline-block',
                                                }}>
                                                  {step.stepExecution.embeddingTonePassed !== false ? '✅' : '❌'} Appropriate Tone
                                                </Typography>
                                              </Box>
                                              
                                              {/* Unbiased - Bottom Right */}
                                              <Box sx={{ 
                                                p: 1.5, 
                                                borderRadius: 2,
                                                bgcolor: step.stepExecution.embeddingBiasPassed !== false ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                                                border: `1px solid ${step.stepExecution.embeddingBiasPassed !== false ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                                              }}>
                                                <Typography variant="caption" sx={{ 
                                                  px: 1.5, py: 0.5, borderRadius: 1,
                                                  bgcolor: step.stepExecution.embeddingBiasPassed !== false ? '#dcfce7' : '#fee2e2',
                                                  fontWeight: 'bold',
                                                  display: 'inline-block',
                                                }}>
                                                  {step.stepExecution.embeddingBiasPassed !== false ? '✅' : '❌'} Unbiased
                                                </Typography>
                                                {step.stepExecution.embeddingBiasPassed === false && (
                                                  <Typography variant="caption" sx={{ color: '#dc2626', fontStyle: 'italic', mt: 1, display: 'block' }}>
                                                    {step.stepExecution.embeddingBiasExplanation || 'Response contained biased content'}
                                                  </Typography>
                                                )}
                                              </Box>
                                            </Box>
                                          )}
                                        </Paper>
                                      </Box>
                                    )}

                                    {/* Resolved Analysis Text */}
                                    {step.stepExecution?.biasAnalysisPerformed && step.stepExecution?.resolvedAnalysisText && (
                                      <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                          📝 Resolved Analysis Text (with variables populated)
                                        </Typography>
                                        <Paper sx={{ p: 1.5, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                                          <Typography 
                                            variant="caption" 
                                            sx={{ 
                                              display: 'block', 
                                              fontFamily: 'monospace', 
                                              fontSize: '0.75rem',
                                              whiteSpace: 'pre-wrap',
                                              wordBreak: 'break-word'
                                            }}
                                          >
                                            {step.stepExecution.resolvedAnalysisText}
                                          </Typography>
                                        </Paper>
                                      </Box>
                                    )}

                                    {/* AI Prompt Used */}
                                    {step.stepExecution?.biasAnalysisPerformed && step.stepExecution?.biasAnalysisPrompt && (
                                      <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                                          📝 AI Prompt Template
                                        </Typography>
                                        <Paper sx={{ p: 1.5, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
                                          <Typography 
                                            variant="caption" 
                                            sx={{ 
                                              display: 'block', 
                                              fontFamily: 'monospace', 
                                              fontSize: '0.75rem',
                                              whiteSpace: 'pre-wrap',
                                              wordBreak: 'break-word'
                                            }}
                                          >
                                            {step.stepExecution.biasAnalysisPrompt}
                                          </Typography>
                                        </Paper>
                                      </Box>
                                    )}
                                  </Box>
                                </Collapse>
                              </Card>
                            ))
                          )}
                        </Box>
                      </Collapse>
                    </Card>
                  ))}
                  
                  {/* Infinite scroll trigger */}
                  {getVisibleCount(group.apiConfig) < group.executions.length && (
                    <InfiniteScrollTrigger 
                      onLoadMore={() => loadMore(group.apiConfig)} 
                      hasMore={getVisibleCount(group.apiConfig) < group.executions.length}
                      totalCount={group.executions.length}
                      visibleCount={getVisibleCount(group.apiConfig)}
                    />
                  )}
                </Box>
              </Collapse>
            </Card>
          ))}
        </Box>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
      >
        <DialogTitle sx={{ bgcolor: '#fee2e2', color: '#991b1b' }}>
          🗑️ Delete Execution
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography>
            Are you sure you want to delete this execution?
          </Typography>
          {executionToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Sequence:</strong> {executionToDelete.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Executed:</strong> {executionToDelete.timestamp}
              </Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action will be logged in the audit trail for compliance purposes.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            data-ga-label="Delete execution Cancel"
            onClick={() => setDeleteConfirmOpen(false)} 
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button 
            data-ga-label="Delete execution Confirm"
            variant="contained" 
            color="error"
            onClick={confirmDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem data-ga-label="Export CSV" onClick={handleExportCsv} disabled={exporting}>
          <TableChart sx={{ mr: 1 }} fontSize="small" />
          Export as CSV
        </MenuItem>
        <MenuItem data-ga-label="Export PDF" onClick={handleExportPdf} disabled={exporting}>
          <PictureAsPdf sx={{ mr: 1 }} fontSize="small" />
          Export as PDF
        </MenuItem>
      </Menu>

      {/* Export loading overlay */}
      {exporting && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <Paper sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="body1">Exporting...</Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

// Infinite scroll trigger component with IntersectionObserver
interface InfiniteScrollTriggerProps {
  onLoadMore: () => void;
  hasMore: boolean;
  totalCount: number;
  visibleCount: number;
}

const InfiniteScrollTrigger: React.FC<InfiniteScrollTriggerProps> = ({ 
  onLoadMore, 
  hasMore, 
  totalCount, 
  visibleCount 
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );
    
    if (triggerRef.current) {
      observer.observe(triggerRef.current);
    }
    
    return () => observer.disconnect();
  }, [onLoadMore, hasMore]);
  
  return (
    <Box 
      ref={triggerRef} 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        py: 2, 
        ml: 4,
        gap: 2
      }}
    >
      <CircularProgress size={20} />
      <Typography variant="body2" color="text.secondary">
        Loading more... ({visibleCount} of {totalCount})
      </Typography>
    </Box>
  );
};

export default BiasResults;